<div class="email-editor-container">
  <h2>Email Configuration</h2>
  <p class="description">Configure Microsoft Graph API credentials for sending emails from this tenant.</p>

  @if (isLoading()) {
    <div class="loading-container">
      <div class="spinner"></div>
      <p>Loading configuration...</p>
    </div>
  }

  @if (!isLoading()) {
    <!-- Local Development Mode Banner -->
    @if (isLocalDevelopment()) {
      <div class="info-banner local-dev-banner">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        <div>
          <strong>Local Development Mode</strong>
          <p>Configuration is loaded from <code>dev.settings.json</code> (GraphApi section). Changes made here will not be saved. Database queries are skipped in local development.</p>
        </div>
      </div>
    }

    <div class="editor-tabs">
      <button 
        type="button" 
        [class.active]="activeTab() === 'configuration'" 
        (click)="setActiveTab('configuration')"
      >
        Configuration
      </button>
      <button 
        type="button" 
        [class.active]="activeTab() === 'invitation'" 
        (click)="setActiveTab('invitation')"
      >
        Invitation Email
      </button>
      <button 
        type="button" 
        [class.active]="activeTab() === 'notification'" 
        (click)="setActiveTab('notification')"
      >
        Notification Email
      </button>
    </div>

    <div class="card">
      <form (ngSubmit)="onSubmit()" #emailForm="ngForm">
        
        <!-- Success Message -->
        @if (successMessage()) {
          <div class="alert alert-success">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span>{{ successMessage() }}</span>
          </div>
        }

        <!-- Error Message -->
        @if (errorMessage()) {
          <div class="alert alert-error">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <span>{{ errorMessage() }}</span>
          </div>
        }

        <!-- Configuration Tab Content -->
        @if (activeTab() === 'configuration') {
          <!-- Graph Tenant ID -->
          <div class="form-group">
            <label for="graphTenantId">
              Azure AD Tenant ID
              <span class="required">*</span>
            </label>
            <input
              type="text"
              id="graphTenantId"
              name="graphTenantId"
              [(ngModel)]="formData.graphTenantId"
              placeholder="e.g., f8fb9326-c907-424e-9e87-2e2eac83dcd5"
              required
              [disabled]="isSaving()"
            />
            <small class="help-text">The Azure AD tenant ID where your app registration exists</small>
          </div>

          <!-- Graph Client ID -->
          <div class="form-group">
            <label for="graphClientId">
              Application (Client) ID
              <span class="required">*</span>
            </label>
            <input
              type="text"
              id="graphClientId"
              name="graphClientId"
              [(ngModel)]="formData.graphClientId"
              placeholder="e.g., d07ff728-b899-404d-bd11-c66603563667"
              required
              [disabled]="isSaving()"
            />
            <small class="help-text">The application (client) ID from your Azure AD app registration</small>
          </div>

          <!-- Client Secret -->
          <div class="form-group">
            <label for="graphClientSecret">
              Client Secret
            </label>
            <input
              type="password"
              id="graphClientSecret"
              name="graphClientSecret"
              autocomplete="off"
              [(ngModel)]="formData.graphClientSecret"
              [placeholder]="isSecretConfigured() ? '•••••••• (Configured)' : 'Enter client secret'"
              [disabled]="isSaving()"
            />
            <small class="help-text">Azure AD client secret for Graph API authentication. Leave empty to keep existing.</small>
          </div>

          <!-- System Sender Email -->
          <div class="form-group">
            <label for="systemSenderEmail">
              System Sender Email
              <span class="required">*</span>
            </label>
            <input
              type="email"
              id="systemSenderEmail"
              name="systemSenderEmail"
              [(ngModel)]="formData.systemSenderEmail"
              placeholder="e.g., noreply@yourdomain.com"
              required
              [disabled]="isSaving()"
            />
            <small class="help-text">The mailbox that will be used to send emails (must have Mail.Send permissions)</small>
          </div>

          <!-- Is Active -->
          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                [(ngModel)]="formData.isActive"
                [disabled]="isSaving()"
              />
              <span>Configuration is active</span>
            </label>
            <small class="help-text">When inactive, email sending will be disabled for this tenant. Only active configurations can send emails.</small>
          </div>
        }

        <!-- Invitation Email Tab Content -->
        @if (activeTab() === 'invitation') {
          <div class="form-group">
            <label for="invitationEmailSubject">
              Email Subject
            </label>
            <input
              type="text"
              id="invitationEmailSubject"
              name="invitationEmailSubject"
              [(ngModel)]="formData.invitationEmailSubject"
              placeholder="Portal Invitation"
              [disabled]="isSaving()"
            />
            <small class="help-text">The subject line for the invitation email.</small>
          </div>

          <div class="form-group invitation-message-group">
            <label for="invitationEmailBody">
              Email Message
            </label>
            <textarea
              id="invitationEmailBody"
              name="invitationEmailBody"
              [(ngModel)]="formData.invitationEmailBody"
              placeholder="Enter a custom message to include in the invitation email..."
              rows="15"
              class="message-textarea"
              [disabled]="isSaving()"
            ></textarea>
            <small class="help-text">This message will be displayed in the body of the invitation email.</small>
          </div>
        }

        <!-- Notification Email Tab Content -->
        @if (activeTab() === 'notification') {
          <!-- Enable Notification Emails -->
          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                id="isNotificationEmailEnabled"
                name="isNotificationEmailEnabled"
                [(ngModel)]="formData.isNotificationEmailEnabled"
                [disabled]="isSaving()"
              />
              <span>Enable notification emails</span>
            </label>
            <small class="help-text">When enabled, users will receive email notifications for new portal notifications.</small>
          </div>

          <div class="form-group">
            <label for="notificationEmailSubject">
              Email Subject
            </label>
            <input
              type="text"
              id="notificationEmailSubject"
              name="notificationEmailSubject"
              [(ngModel)]="formData.notificationEmailSubject"
              placeholder="Portal Notification"
              [disabled]="isSaving() || !formData.isNotificationEmailEnabled"
            />
            <small class="help-text">The subject line for notification emails.</small>
          </div>

          <div class="form-group invitation-message-group">
            <label for="notificationEmailBody">
              Email Message
            </label>
            <textarea
              id="notificationEmailBody"
              name="notificationEmailBody"
              [(ngModel)]="formData.notificationEmailBody"
              placeholder="Enter a custom message to include in notification emails..."
              rows="15"
              class="message-textarea"
              [disabled]="isSaving() || !formData.isNotificationEmailEnabled"
            ></textarea>
            <small class="help-text">This message will be displayed as the first paragraph in notification emails.</small>
          </div>
        }

        <!-- Form Actions -->
        <div class="form-actions">
          <button
            type="button"
            class="btn btn-secondary"
            (click)="onCancel()"
            [disabled]="isSaving()"
          >
            Cancel
          </button>
          @if (activeTab() === 'invitation') {
            <button
              type="button"
              class="btn btn-secondary"
              data-test="send-test-invitation"
              [disabled]="isSendingInvitationTest() || isSaving() || isLoading()"
              (click)="sendTestInvitationEmail()"
            >
              @if (isSendingInvitationTest()) {
                <span class="btn-spinner"></span>
                <span>Sending...</span>
              } @else {
                <span>Send Test Email</span>
              }
            </button>
          }
          @if (activeTab() === 'notification') {
            <button
              type="button"
              class="btn btn-secondary"
              data-test="send-test-notification"
              [disabled]="isSendingNotificationTest() || isSaving() || isLoading()"
              (click)="sendTestNotificationEmail()"
            >
              @if (isSendingNotificationTest()) {
                <span class="btn-spinner"></span>
                <span>Sending...</span>
              } @else {
                <span>Send Test Email</span>
              }
            </button>
          }
          <button
            type="submit"
            class="btn btn-primary"
            [disabled]="isSaving() || !emailForm.form.valid"
          >
            @if (isSaving()) {
              <span class="btn-spinner"></span>
              <span>Saving...</span>
            } @else {
              <span>{{ hasExistingConfig() ? 'Update Configuration' : 'Save Configuration' }}</span>
            }
          </button>
        </div>
      </form>
    </div>

    <!-- Information Panel -->
    <div class="info-panel">
      <h3>Setup Instructions</h3>
      <ol>
        <li>Create an app registration in Azure AD</li>
        <li>Grant <strong>Mail.Send</strong> application permission to Microsoft Graph</li>
        <li>Create a client secret for the app registration</li>
        <li>Ensure the system sender email has a valid mailbox</li>
        <li>Grant the app permission to send as the system sender email</li>
      </ol>
      
      <div class="warning-box">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
        <div>
          <strong>Security Note:</strong>
          <p>Client secrets are stored securely in Azure Key Vault and are never transmitted or displayed in full.</p>
        </div>
      </div>
    </div>
  }
</div>
